// Admin authentication — DynamoDB-backed
// Stores admin accounts in BadgeSubmission table with formType: 'adminAccount'
// Supports: login, create account, change password
// Falls back to hardcoded defaults if DynamoDB is unavailable
import { API } from 'aws-amplify';
import { createBadgeSubmission, updateBadgeSubmission } from '../graphql/mutations';
import { getBadgeSubmission, listBadgeSubmissions } from '../graphql/queries';

// Default admin accounts (hardcoded fallback)
// Hash = SHA-256(password + email + 'salt2024')
const DEFAULT_ADMINS = {
  'admin@amazon.com': '1ca22802b11b9c75295a9e6d49e1c2b3faf5ca2b50185cc0ffe238d39efcd4fb', // admin123
  'photostudio@amazon.com': '87f2ae6e7ff591009a76288b0acd1fbc0a2b623072c2251b224a9179970c2f8b' // photo123
};

/**
 * Hash a password with email salt using Web Crypto API
 */
export const hashPassword = async (password, email) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(password + email.toLowerCase().trim() + 'salt2024');
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
};

/**
 * Get admin account ID for DynamoDB
 */
const adminId = (email) => `admin-${email.toLowerCase().trim()}`;

/**
 * Authenticate admin user
 * Checks DynamoDB first, then falls back to hardcoded defaults
 */
export const authenticateAdmin = async (email, password) => {
  try {
    if (!email || !password) return false;
    const emailKey = email.toLowerCase().trim();
    const hashedInput = await hashPassword(password, emailKey);

    // Check DynamoDB first
    try {
      const result = await API.graphql({
        query: getBadgeSubmission,
        variables: { id: adminId(emailKey) }
      });
      const account = result.data?.getBadgeSubmission;
      if (account && account.formType === 'adminAccount' && account.tokenNumber === hashedInput) {
        return true;
      }
      // Account exists in DynamoDB but password doesn't match
      if (account && account.formType === 'adminAccount') {
        return false;
      }
    } catch (dbError) {
      console.warn('DynamoDB auth check failed, trying defaults:', dbError.message);
    }

    // Fallback to hardcoded defaults
    if (DEFAULT_ADMINS[emailKey] && DEFAULT_ADMINS[emailKey] === hashedInput) {
      return true;
    }

    return false;
  } catch (error) {
    console.error('Authentication error:', error);
    return false;
  }
};

/**
 * Create a new admin account in DynamoDB
 */
export const createAdminAccount = async (email, password) => {
  try {
    const emailKey = email.toLowerCase().trim();
    
    if (!emailKey.endsWith('@amazon.com')) {
      return { success: false, message: 'Email must be an @amazon.com address' };
    }
    if (!password || password.length < 6) {
      return { success: false, message: 'Password must be at least 6 characters' };
    }

    // Check if account already exists
    try {
      const existing = await API.graphql({
        query: getBadgeSubmission,
        variables: { id: adminId(emailKey) }
      });
      if (existing.data?.getBadgeSubmission) {
        return { success: false, message: 'Account with this email already exists' };
      }
    } catch (e) {
      // Not found — good, we can create
    }

    const hashedPassword = await hashPassword(password, emailKey);

    await API.graphql({
      query: createBadgeSubmission,
      variables: {
        input: {
          id: adminId(emailKey),
          email: emailKey,
          siteCode: 'ADMIN',
          formType: 'adminAccount',
          tokenNumber: hashedPassword, // Store hash in tokenNumber field
          status: 'Active',
          submissionDate: new Date().toISOString()
        }
      }
    });

    return { success: true, message: 'Account created successfully' };
  } catch (error) {
    console.error('Create account error:', error);
    const msg = error.errors ? error.errors.map(e => e.message).join(', ') : error.message;
    return { success: false, message: msg || 'Failed to create account' };
  }
};

/**
 * Change password for an existing admin account
 * Requires current password for verification
 */
export const changePassword = async (email, currentPassword, newPassword) => {
  try {
    const emailKey = email.toLowerCase().trim();

    if (!newPassword || newPassword.length < 6) {
      return { success: false, message: 'New password must be at least 6 characters' };
    }

    // Verify current password
    const isValid = await authenticateAdmin(emailKey, currentPassword);
    if (!isValid) {
      return { success: false, message: 'Current password is incorrect' };
    }

    const newHash = await hashPassword(newPassword, emailKey);

    // Check if account exists in DynamoDB
    try {
      const existing = await API.graphql({
        query: getBadgeSubmission,
        variables: { id: adminId(emailKey) }
      });

      if (existing.data?.getBadgeSubmission) {
        // Update existing DynamoDB account
        await API.graphql({
          query: updateBadgeSubmission,
          variables: {
            input: {
              id: adminId(emailKey),
              tokenNumber: newHash
            }
          }
        });
        return { success: true, message: 'Password changed successfully' };
      }
    } catch (e) {
      // Account not in DynamoDB yet
    }

    // Account is a hardcoded default — create a DynamoDB entry to override it
    await API.graphql({
      query: createBadgeSubmission,
      variables: {
        input: {
          id: adminId(emailKey),
          email: emailKey,
          siteCode: 'ADMIN',
          formType: 'adminAccount',
          tokenNumber: newHash,
          status: 'Active',
          submissionDate: new Date().toISOString()
        }
      }
    });

    return { success: true, message: 'Password changed successfully' };
  } catch (error) {
    console.error('Change password error:', error);
    const msg = error.errors ? error.errors.map(e => e.message).join(', ') : error.message;
    return { success: false, message: msg || 'Failed to change password' };
  }
};

/**
 * Check if user is currently logged in
 */
export const getCurrentAdmin = () => {
  try {
    return localStorage.getItem('adminUser');
  } catch (error) {
    return null;
  }
};

/**
 * Logout current admin
 */
export const logoutAdmin = () => {
  try {
    localStorage.removeItem('adminUser');
    localStorage.removeItem('adminLoginTime');
  } catch (error) {
    console.error('Logout error:', error);
  }
};
